<style lang="scss" scoped>
// Variables/functions only — no selectors, so importing these into a scoped
// SFC style block doesn't duplicate any compiled CSS. Lets this file
// reference the same tokens as _variables.scss instead of re-declaring
// their hex values (the source of every "undocumented color" drift the
// 2026-07-19 audit found in this file).
@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';
@import '@/assets/sass/_variables.scss';

.table td {
  vertical-align: middle;
  height: 20px !important;
}

#tblCom tbody tr td,
#tblLineFee tbody tr td {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}

/* Modern table treatment for dashboard data tables */
.table-modern {
  margin-bottom: 0;
}
.table-modern thead th {
  background-color: $gray-100;
  color: $brand-navy;
  font-weight: 600;
  font-size: 0.8125rem;
  border-top: none;
  border-bottom: 2px solid $gray-300;
  white-space: nowrap;
}
.table-modern tbody td {
  border-top: 1px solid $gray-100;
}
.table-modern tbody tr:hover {
  background-color: rgba($brand-navy, 0.035);
}
.table-modern tfoot th {
  border-top: 2px solid $gray-300;
}

.w-80px {
  min-width: 80px !important;
}

.w-100px {
  min-width: 100px !important;
}

.w-150px {
  min-width: 150px !important;
}

.w-200px {
  min-width: 200px !important;
}
.w-250px {
  min-width: 250px !important;
}

/* Redesigned Sidebar and Dashboard Styles */
.cursor-pointer {
  cursor: pointer;
}
.bg-light-sidebar {
  background-color: #fafbfc;
}
.active-pill {
  // Was a stale linear-gradient(105deg, #002e5b, #2f7fd4) — #2f7fd4 is the
  // pre-2026-07-16 sky value, replaced everywhere else after it failed the
  // white-text AA check (see _variables.scss $brand-sky note). $gradient-end
  // brings this pill back in sync with the corrected, AA-safe sky.
  background: linear-gradient(105deg, $gradient-start, $gradient-end) !important;
  color: $white !important;
  box-shadow: 0 4px 12px rgba($brand-navy, 0.15);
  font-weight: 600;
}
.active-pill i {
  color: $white !important;
}
.nav-pills .nav-link {
  border-radius: 8px;
  color: #495057;
  padding: 10px 14px;
  transition: all 0.2s ease;
}
.nav-pills .nav-link:hover:not(.active-pill) {
  background-color: rgba($brand-sky, 0.08);
  color: $brand-navy;
}

/* Soft Badges */
/* Text darkened off the raw semantic hue for WCAG AA (~4.5:1) — the
   raw success/warning hues fail at badge size on their own soft tint. */
.badge-soft-success {
  background-color: rgba(40, 167, 69, 0.12) !important;
  color: #1e7e34 !important;
  font-weight: 600;
}
.badge-soft-warning {
  background-color: rgba($orange, 0.12) !important;
  color: #b95c00 !important;
  font-weight: 600;
}
.badge-soft-danger {
  background-color: rgba(220, 53, 69, 0.12) !important;
  color: #dc3545 !important;
  font-weight: 600;
}
.badge-soft-primary {
  background-color: rgba($brand-navy, 0.08) !important;
  color: $brand-navy !important;
  font-weight: 600;
}
.badge-soft-secondary {
  background-color: rgba(108, 117, 125, 0.12) !important;
  color: $gray-700 !important;
  font-weight: 600;
}
.bg-soft-primary {
  background-color: rgba($brand-navy, 0.05) !important;
  color: $brand-navy !important;
}
.text-primary-dark {
  color: $brand-navy !important;
}
.bg-soft-danger {
  background-color: rgba($danger, 0.08) !important;
}

/* Welcome card (personal header) */
.welcome-card {
  border-radius: 12px !important;
  overflow: hidden;
}
.welcome-avatar {
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba($white, 0.18);
  color: $white;
  font-size: 1.5rem;
}
.welcome-card__meta {
  color: rgba($white, 0.75);
}
.welcome-expiry-chip {
  display: inline-flex;
  align-items: center;
  background-color: $white;
  color: $danger;
  border-radius: 20px;
  padding: 1px 10px;
  font-weight: 600;
}
.welcome-year {
  line-height: 1.1;
}
.welcome-year__value {
  font-size: 2.5rem;
  font-weight: 700;
  color: $white;
}
.welcome-year__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba($white, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Dribbble Style KPI Cards */
.kpi-card {
  border-radius: 12px !important;
  background-color: $white !important;
  border: none !important;
  box-shadow: 0 4px 16px rgba($brand-navy, 0.10) !important;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba($brand-navy, 0.16) !important;
}

.pending-kpi {
  border-top: 4px solid $orange !important;
}
.personal-kpi {
  border-top: 4px solid #28a745 !important;
}
.team-kpi {
  border-top: 4px solid $brand-sky !important;
}
.font-xs {
  font-size: 0.78rem;
}
.text-warning-dark {
  color: #b95c00 !important;
}
</style>

<template>
  <Layout>
    <div class="pt-3">
      <!-- Welcome Card — the one personal, non-data moment on this dashboard;
           uses the app's existing (until now unused) card-header-brand signature -->
      <div class="card welcome-card border-0 mb-4">
        <div class="card-header card-header-brand d-flex align-items-center flex-wrap py-3">
          <div class="welcome-avatar mr-3">
            <i class="fas fa-user-tie"></i>
          </div>
          <div>
            <h4 class="m-0 font-20 text-white">
              ยินดีต้อนรับสมาชิก AML,
              <span v-if="DashbData.Info.CitizenType == 1">คุณ</span>{{ DashbData.Info.Firstname }} {{ DashbData.Info.Lastname }}
            </h4>
            <span class="welcome-card__meta font-15">
              ใบอนุญาตนายหน้าประกันชีวิตเลขที่: <strong class="text-white">{{ DashbData.Info.BrokerCardNo || '-' }}</strong>
              <span class="mx-2">|</span>
              วันหมดอายุบัตรนายหน้า:
              <strong v-if="AMLExpiredIn > 60" class="text-white">{{ dateThTimeFormat(DashbData.Info.SKLBrokerCardExpired, 'DD MMMM YYYY') }}</strong>
              <span v-else class="welcome-expiry-chip ml-1">{{ dateThTimeFormat(DashbData.Info.SKLBrokerCardExpired, 'DD MMMM YYYY') }}</span>
            </span>
          </div>

          <div class="welcome-year ml-auto text-right">
            <div class="welcome-year__value">{{ YearThTables }}</div>
            <div class="welcome-year__label">ปีที่เลือก</div>
          </div>
        </div>

        <!-- Expiry action strip — only appears when it's actually actionable -->
        <div v-if="!IsLoading && DashbData.Info.SKLBrokerCardExpired && AMLExpiredIn <= 60"
          class="card-body py-2 bg-soft-danger d-flex align-items-center justify-content-between flex-wrap">
          <div class="font-xs m-0 text-danger font-weight-semibold">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            <span v-if="AMLExpiredIn <= 0">บัตรนายหน้าหมดอายุแล้ว</span>
            <span v-else>บัตรนายหน้าจะหมดอายุใน {{ AMLExpiredIn }} วัน</span>
          </div>
          <router-link :to="{ name: 'upload-aml-doc', params: { slug: $store.state.Admin.RefId } }" class="btn btn-danger btn-sm py-1 font-weight-bold font-xs">
            อัปโหลดเอกสารต่ออายุ
          </router-link>
        </div>
      </div>

      <div class="row d-flex align-items-stretch">
        <!-- Card 1: Pending Jobs -->
        <div class="col-xl-4 col-md-4 col-sm-12 mb-3">
          <div class="card border-0 shadow-sm h-100 kpi-card pending-kpi">
            <div class="card-body d-flex flex-column justify-content-between">
              <div>
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="text-dark font-weight-semibold font-17">งานที่อยู่ระหว่างดำเนินการ</span>
                  <div class="avatar-sm rounded-circle d-flex align-items-center justify-content-center bg-soft-warning" style="width: 32px; height: 32px;">
                    <i class="fas fa-user-clock text-warning"></i>
                  </div>
                </div>
                <h3 class="text-dark mb-1">
                  <b-spinner v-if="IsLoading" small></b-spinner>
                  {{ DashbData.JobPending.Qty }} <span class="text-muted">รายการ</span>
                </h3>
                <p class="text-muted font-xs mb-3 d-flex align-items-baseline">
                  เบี้ยรวมสะสม:&nbsp;<span class="text-warning-dark font-weight-semibold font-18">{{ moneyFormatFull(DashbData.JobPending.Amount) }}</span>&nbsp;บาท
                </p>
              </div>
              <div class="progress progress-sm bg-light mb-0" style="height: 6px;">
                <div class="progress-bar bg-warning" role="progressbar" style="width: 100%" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 2: Personal Sales -->
        <div class="col-xl-4 col-md-4 col-sm-12 mb-3">
          <div class="card border-0 shadow-sm h-100 kpi-card personal-kpi">
            <div class="card-body d-flex flex-column justify-content-between">
              <div>
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="text-dark font-weight-semibold font-17">ยอดขายสะสมส่วนตัวทั้งปี</span>
                  <div class="avatar-sm rounded-circle d-flex align-items-center justify-content-center bg-soft-success" style="width: 32px; height: 32px;">
                    <i class="fas fa-chart-line text-success"></i>
                  </div>
                </div>
                <h3 class="text-success mb-1">
                  <b-spinner v-if="IsLoading" small></b-spinner>
                  {{ moneyFormatFull(DashbData.PremiumYear.New + DashbData.PremiumYear.Renew) }}
                </h3>
                <span class="font-xs text-muted">บาท</span>
              </div>
              <div class="d-flex justify-content-between font-xs mt-2 pt-2 border-top">
                <div class="border-right pr-2 w-50">
                  <span class="text-muted d-block" style="font-size: 0.7rem;">งานใหม่</span>
                  <span class="text-dark font-weight-semibold">{{ moneyFormatFull(DashbData.PremiumYear.New) }}</span>
                </div>
                <div class="text-right pl-2 w-50">
                  <span class="text-muted d-block" style="font-size: 0.7rem;">งานต่ออายุ</span>
                  <span class="text-dark font-weight-semibold">{{ moneyFormatFull(DashbData.PremiumYear.Renew) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 3: Team Sales -->
        <div class="col-xl-4 col-md-4 col-sm-12 mb-3">
          <div class="card border-0 shadow-sm h-100 kpi-card team-kpi">
            <div class="card-body d-flex flex-column justify-content-between">
              <div>
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="text-dark font-weight-semibold font-17">ยอดขายรวมทีมงานทั้งปี</span>
                  <div class="avatar-sm rounded-circle d-flex align-items-center justify-content-center bg-soft-primary" style="width: 32px; height: 32px;">
                    <i class="fas fa-users text-primary"></i>
                  </div>
                </div>
                <h3 class="text-primary-dark mb-1">
                  <b-spinner v-if="IsLoading" small></b-spinner>
                  {{ moneyFormatFull(DashbData.PremiumYearWithLine.New + DashbData.PremiumYearWithLine.Renew) }}
                </h3>
                <span class="font-xs text-muted">บาท</span>
              </div>
              <div class="d-flex justify-content-between font-xs mt-2 pt-2 border-top">
                <div class="border-right pr-2 w-50">
                  <span class="text-muted d-block" style="font-size: 0.7rem;">งานใหม่</span>
                  <span class="text-dark font-weight-semibold">{{ moneyFormatFull(DashbData.PremiumYearWithLine.New) }}</span>
                </div>
                <div class="text-right pl-2 w-50">
                  <span class="text-muted d-block" style="font-size: 0.7rem;">งานต่ออายุ</span>
                  <span class="text-dark font-weight-semibold">{{ moneyFormatFull(DashbData.PremiumYearWithLine.Renew) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    <div class="row">
      <div class="col-12">
        <b-overlay :show="IsLoadingTab || IsLoading" rounded="sm">
          <b-card no-body>
            <div class="row no-gutters">
              <!-- Left Sidebar Navigation -->
              <div class="col-xl-3 col-lg-4 col-md-12 border-right p-3 bg-light-sidebar">
                <div class="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
                  <b-button variant="outline-primary" size="sm" @click="onChangeYear(-1)"><i class="fe-chevrons-left"></i></b-button>
                  <span class="font-weight-bold text-primary">
                    <i class="far fa-calendar-alt mr-1"></i> ปี {{ YearThTables }}
                  </span>
                  <b-button variant="outline-primary" size="sm" @click="onChangeYear(1)"><i class="fe-chevrons-right"></i></b-button>
                </div>
                <div class="nav flex-column nav-pills" role="tablist">
                  <a class="nav-link mb-2 d-flex align-items-center justify-content-between cursor-pointer"
                     :class="{ 'active-pill': activeTab === 'pending' }"
                     @click="activeTab = 'pending'">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-clock mr-2 text-warning"></i>
                      <span>งานค้างที่ยังไม่อนุมัติ</span>
                    </div>
                    <span class="badge badge-pill" :class="activeTab === 'pending' ? 'badge-light text-primary' : 'badge-soft-secondary'">
                      {{ DashbDataTables.JobPendingList ? DashbDataTables.JobPendingList.length : 0 }}
                    </span>
                  </a>

                  <a class="nav-link mb-2 d-flex align-items-center justify-content-between cursor-pointer"
                     :class="{ 'active-pill': activeTab === 'commissions' }"
                     @click="activeTab = 'commissions'">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-hand-holding-usd mr-2 text-primary"></i>
                      <span>วันที่คืนค่าคอมมิชชั่น</span>
                    </div>
                    <span class="badge badge-pill" :class="activeTab === 'commissions' ? 'badge-light text-primary' : 'badge-soft-secondary'">
                      {{ DashbDataTables.Commissions ? DashbDataTables.Commissions.length : 0 }}
                    </span>
                  </a>

                  <a class="nav-link mb-2 d-flex align-items-center justify-content-between cursor-pointer"
                     :class="{ 'active-pill': activeTab === 'linefees' }"
                     @click="activeTab = 'linefees'">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-users mr-2 text-success"></i>
                      <span>คืนค่าสายงาน/แนะนำ</span>
                    </div>
                    <span class="badge badge-pill" :class="activeTab === 'linefees' ? 'badge-light text-primary' : 'badge-soft-secondary'">
                      {{ DashbDataTables.LineFees ? DashbDataTables.LineFees.length : 0 }}
                    </span>
                  </a>

                  <a class="nav-link mb-2 d-flex align-items-center justify-content-between cursor-pointer"
                     :class="{ 'active-pill': activeTab === 'docs' }"
                     @click="activeTab = 'docs'">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-envelope mr-2 text-info"></i>
                      <span>จดหมายและพัสดุ</span>
                    </div>
                    <span class="badge badge-pill" :class="activeTab === 'docs' ? 'badge-light text-primary' : 'badge-soft-secondary'">
                      {{ DashbDataTables.AgentDocs ? DashbDataTables.AgentDocs.length : 0 }}
                    </span>
                  </a>
                </div>
              </div>

              <!-- Right Content Area -->
              <div class="col-xl-9 col-lg-8 col-md-12 p-0">
                <!-- Tab 1: Pending Jobs -->
                <div v-show="activeTab === 'pending'">
                  <div class="table-responsive mb-0">
                    <table id="tblJobPending" class="table table-sm table-centered table-modern border-0">
                      <thead class="thead-light">
                        <tr>
                          <th class="text-center" style="width: 50px;">ลำดับ</th>
                          <th class="text-center">ผู้เอาประกัน</th>
                          <th class="text-center">กรมธรรม์</th>
                          <th class="text-center">สถานะ</th>
                          <th class="text-right">เบี้ย</th>
                          <th class="text-center">ชำระเบี้ยแบบ</th>
                          <th class="text-center">ชำระพร้อมใบคำขอ(งวด)</th>
                          <th class="text-center">บ.ประกัน</th>
                          <th class="text-center">วันที่แจ้งงาน</th>
                          <th class="text-center">ผู้แจ้งงาน</th>
                          <th class="text-center">สาขา</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, idx) of DashbDataTables.JobPendingList" :key="idx">
                          <td class="text-center">{{ idx + 1 }}</td>
                          <td class="text-center text-nowrap">{{ row.Firstname }} {{ row.Lastname }}</td>
                          <td class="text-center"><strong>{{ row.ContactPolicyNumber }}</strong></td>
                          <td class="text-center">
                            <span :class="getStatusBadgeClass(row.OrderStatus)">
                              {{ row.OrderStatus }}
                            </span>
                          </td>
                          <td class="text-right font-weight-semibold text-primary-dark">{{ moneyFormatFull(row.PaymentAmount) }}</td>
                          <td class="text-center text-nowrap">{{ row.PaymentTypeTxt }}</td>
                          <td class="text-center text-nowrap">{{ row.PaymentTime == 0 ? '-' : row.PaymentTime }}</td>
                          <td class="text-center text-nowrap">{{ row.InsuranceTxt }}</td>
                          <td class="text-center text-nowrap">{{ dateThTimeFormat(row.CreatedAt, 'DD/MM/YYYY') }}</td>
                          <td class="text-center text-nowrap">{{ row.AdminTxt }}</td>
                          <td class="text-center text-nowrap">{{ row.BranchTxt }}</td>
                        </tr>
                        <tr v-if="!DashbDataTables.JobPendingList || DashbDataTables.JobPendingList.length === 0">
                          <td colspan="100%" class="table-empty-note">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Tab 2: Commissions -->
                <div v-show="activeTab === 'commissions'">
                  <div class="table-responsive mb-0">
                    <table id="tblCom" class="table table-sm table-centered table-modern border-0">
                      <thead class="thead-light">
                        <tr>
                          <th></th>
                          <th></th>
                          <th></th>
                          <th></th>
                          <th v-if="DashbData.Info.CitizenType == 1" class="text-center bg-info" colspan="2">กรณีไม่จดทะเบียน VAT</th>
                          <th v-if="DashbData.Info.CitizenType == 2" class="text-center bg-info" colspan="2">กรณีจดทะเบียน VAT</th>
                          <th></th>
                          <th></th>
                        </tr>
                        <tr>
                          <th class="text-center" style="width: 50px;">ลำดับ</th>
                          <th class="text-right text-wrap">ค่าคอมมิชชั่น<br />(รวม VAT)</th>
                          <th class="text-right text-wrap">ค่าคอมมิชชั่น<br />(ไม่รวม VAT)</th>
                          <th class="text-right">ค่าธรรมเนียม</th>
                          <th v-if="DashbData.Info.CitizenType == 1" class="text-right bg-info text-primary-dark">WHT</th>
                          <th v-if="DashbData.Info.CitizenType == 1" class="text-right bg-info text-primary-dark">ยอดโอนค่าคอมมิชชั่น</th>
                          <th v-if="DashbData.Info.CitizenType == 2" class="text-right bg-info text-primary-dark">WHT</th>
                          <th v-if="DashbData.Info.CitizenType == 2" class="text-right bg-info text-primary-dark">ยอดโอนค่าคอมมิชชั่น</th>
                          <th class="text-center">วันที่จ่าย (วว/ดด/ปปปป)</th>
                          <th class="text-center">รายละเอียด</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, idx) of DashbDataTables.Commissions" :key="idx">
                          <td class="text-center">{{ idx + 1 }}</td>
                          <td class="text-right font-weight-semibold text-primary-dark">{{ moneyFormatFull(row.Commission) }}</td>
                          <td class="text-right font-weight-semibold text-muted">{{ moneyFormatFull(row.CommissionAfterTax) }}</td>
                          <td class="text-right text-danger">{{ row.PaymentCreditCard ? moneyFormatFull(row.PaymentCreditCard) : '-' }}</td>
                          <td v-if="DashbData.Info.CitizenType == 1" class="text-right text-muted">-</td>
                          <td v-if="DashbData.Info.CitizenType == 1" class="text-right font-weight-semibold text-success">{{ moneyFormatFull(calculateTransferNoVat(row.CommissionAfterTax, row.PaymentCreditCard)) }}</td>
                          <td v-if="DashbData.Info.CitizenType == 2" class="text-right font-weight-semibold text-danger">{{ moneyFormatFull(calculateWhtVat(row.CommissionAfterTax)) }}</td>
                          <td v-if="DashbData.Info.CitizenType == 2" class="text-right font-weight-bold text-success">{{ moneyFormatFull(calculateTransferVat(row.Commission, row.PaymentCreditCard, row.CommissionAfterTax)) }}</td>
                          <td class="text-center">{{ dateThTimeFormat(row.CommissionAt, 'DD/MM/YYYY') }}</td>
                          <td class="text-center">
                            <b-button variant="outline-primary" size="sm" @click="showCommDetail(row)">
                              <i class="fe-search"></i>
                            </b-button>
                          </td>
                        </tr>
                        <tr v-if="!DashbDataTables.Commissions || DashbDataTables.Commissions.length === 0">
                          <td colspan="100%" class="table-empty-note">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                      <tfoot v-if="DashbDataTables.Commissions && DashbDataTables.Commissions.length > 0" class="bg-light text-dark font-weight-bold">
                        <tr>
                          <th class="text-center">รวม</th>
                          <th class="text-right text-primary-dark">{{moneyFormatFull(getSum(DashbDataTables.Commissions, 'Commission'))}}</th>
                          <th class="text-right text-muted">{{moneyFormatFull(getSum(DashbDataTables.Commissions, 'CommissionAfterTax'))}}</th>
                          <th class="text-right text-danger">{{moneyFormatFull(getSum(DashbDataTables.Commissions, 'PaymentCreditCard'))}}</th>
                          <th v-if="DashbData.Info.CitizenType == 1" class="text-right">-</th>
                          <th v-if="DashbData.Info.CitizenType == 1" class="text-right text-success">{{moneyFormatFull(getSumTransferNoVat(DashbDataTables.Commissions))}}</th>
                          <th v-if="DashbData.Info.CitizenType == 2" class="text-right text-danger">{{moneyFormatFull(getSumWhtVat(DashbDataTables.Commissions))}}</th>
                          <th v-if="DashbData.Info.CitizenType == 2" class="text-right text-success">{{moneyFormatFull(getSumTransferVat(DashbDataTables.Commissions))}}</th>
                          <th class="text-right"></th>
                          <th></th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <!-- Tab 3: Line Fees -->
                <div v-show="activeTab === 'linefees'">
                  <div class="col-xl-12 col-sm-12 px-0">
                    <div class="table-responsive mb-0">
                      <table id="tblLineFee" class="table table-sm table-centered table-modern border-0">
                        <thead class="thead-light">
                          <tr>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th v-if="DashbData.Info.CitizenType == 1" class="text-center bg-info" colspan="2">กรณีไม่จดทะเบียน VAT</th>
                            <th v-if="DashbData.Info.CitizenType == 2" class="text-center bg-info" colspan="2">กรณีจดทะเบียน VAT</th>
                            <th></th>
                          </tr>
                          <tr>
                            <th class="text-center" style="width: 50px;">ลำดับ</th>
                            <th class="text-right">ค่าบริหารทีม (รวม VAT)</th>
                            <th class="text-right">ค่าบริหารทีม (ไม่รวม VAT)</th>
                            <th v-if="DashbData.Info.CitizenType == 1" class="text-right bg-info text-primary-dark">WHT ภงด1</th>
                            <th v-if="DashbData.Info.CitizenType == 1" class="text-right bg-info text-primary-dark">ยอดโอนค่าบริหารทีม</th>
                            <th v-if="DashbData.Info.CitizenType == 2" class="text-right bg-info text-primary-dark">WHT</th>
                            <th v-if="DashbData.Info.CitizenType == 2" class="text-right bg-info text-primary-dark">ยอดโอนค่าค่าบริหารทีม</th>
                            <th class="text-center">วันที่จ่าย (วว/ดด/ปปปป)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(row, idx) of DashbDataTables.LineFees" :key="idx">
                            <td class="text-center">{{ idx + 1 }}</td>
                            <td class="text-right font-weight-semibold text-primary-dark">{{ moneyFormatFull(row.Commission) }}</td>
                            <td class="text-right font-weight-semibold text-muted">{{ moneyFormatFull(row.CommissionAfterTax) }}</td>
                            <td v-if="DashbData.Info.CitizenType == 1" class="text-right text-muted">-</td>
                            <td v-if="DashbData.Info.CitizenType == 1" class="text-right font-weight-semibold text-success">{{ moneyFormatFull(calculateTransferNoVat(row.CommissionAfterTax, row.PaymentCreditCard)) }}</td>
                            <td v-if="DashbData.Info.CitizenType == 2" class="text-right font-weight-semibold text-danger">{{ moneyFormatFull(calculateWhtVat(row.CommissionAfterTax)) }}</td>
                            <td v-if="DashbData.Info.CitizenType == 2" class="text-right font-weight-bold text-success">{{ moneyFormatFull(calculateTransferVat(row.Commission, row.PaymentCreditCard, row.CommissionAfterTax)) }}</td>
                            <td class="text-center">{{ dateThTimeFormat(row.CommissionAt, 'DD/MM/YYYY') }}</td>
                          </tr>
                          <tr v-if="!DashbDataTables.LineFees || DashbDataTables.LineFees.length === 0">
                            <td colspan="100%" class="table-empty-note">ไม่มีข้อมูล</td>
                          </tr>
                        </tbody>
                        <tfoot v-if="DashbDataTables.LineFees && DashbDataTables.LineFees.length > 0" class="bg-light text-dark font-weight-bold">
                          <tr>
                            <th class="text-center">รวม</th>
                            <th class="text-right text-primary-dark">{{moneyFormatFull(getSum(DashbDataTables.LineFees, 'Commission'))}}</th>
                            <th class="text-right text-muted">{{moneyFormatFull(getSum(DashbDataTables.LineFees, 'CommissionAfterTax'))}}</th>
                            <th v-if="DashbData.Info.CitizenType == 1" class="text-right">-</th>
                            <th v-if="DashbData.Info.CitizenType == 1" class="text-right text-success">{{moneyFormatFull(getSumTransferNoVat(DashbDataTables.LineFees))}}</th>
                            <th v-if="DashbData.Info.CitizenType == 2" class="text-right text-danger">{{moneyFormatFull(getSumWhtVat(DashbDataTables.LineFees))}}</th>
                            <th v-if="DashbData.Info.CitizenType == 2" class="text-right text-success">{{moneyFormatFull(getSumTransferVat(DashbDataTables.LineFees))}}</th>
                            <th class="text-right"></th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- Tab 4: Docs -->
                <div v-show="activeTab === 'docs'">
                  <div class="table-responsive mb-0">
                    <table id="tblDocs" class="table table-sm table-centered table-modern border-0">
                      <thead class="thead-light">
                        <tr>
                          <th class="text-center" style="width: 50px;">ลำดับ</th>
                          <th scope="col" class="text-nowrap">วันที่รับเอกสาร</th>
                          <th scope="col" class="text-nowrap">วิธีรับเอกสาร</th>
                          <th scope="col" class="text-nowrap">ประเภทเอกสาร</th>
                          <th scope="col" class="text-nowrap">เลขกรมธรรม์</th>
                          <th scope="col" class="text-nowrap">Remark</th>
                          <th scope="col" class="text-nowrap">วันที่ส่งเอกสาร</th>
                          <th scope="col" class="text-nowrap">วิธีส่งเอกสาร</th>
                          <th scope="col" class="text-nowrap text-left">Admin ผู้รับ</th>
                          <th scope="col" class="text-nowrap text-center">วันที่ทำรายการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, idx) of DashbDataTables.AgentDocs" :key="idx">
                          <td class="text-center">{{ idx + 1 }}</td>
                          <td class="text-center">{{ dateThTimeFormat(row.RcvDate, 'DD/MM/YYYY') }}</td>
                          <td class="text-nowrap text-left">{{ row.RcvMethod }}</td>
                          <td class="text-nowrap text-left">{{ row.DocType }}</td>
                          <td class="text-left"><strong>{{ row.PostCode }}</strong></td>
                          <td class="text-nowrap text-left text-muted">{{ row.Remark }}</td>
                          <td class="text-center">{{ dateThTimeFormat(row.SendDate, 'DD/MM/YYYY') }}</td>
                          <td class="text-nowrap text-left">{{ row.SendMethod }}</td>
                          <td class="text-nowrap text-left">{{ row.AdminTxt }}</td>
                          <td class="text-center text-muted">{{ dateThTimeFormat(row.CreatedAt, 'DD/MM/YYYY') }}</td>
                        </tr>
                        <tr v-if="!DashbDataTables.AgentDocs || DashbDataTables.AgentDocs.length === 0">
                          <td colspan="100%" class="table-empty-note">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </b-card>
        </b-overlay>
      </div>
    </div>

    <b-modal size="xl" id="bv-modal-detail" hide-footer small>
      <template #modal-title>
        รายละเอียด ค่าคอมมิชชั่น
      </template>
      <div class="d-block">
        <div class="row">
          <div class="col-12 text-center mb-3">
            <h4 class="text-dark">วันที่ {{ dateThTimeFormat(CurrComm.CommissionAt, 'DD-MM-YYYY') }}</h4>
          </div>
        </div>
        <div class="row p-0 pt-0">
          <div class="col-md-12">

            <div class="table-responsivex">
              <table class="table table-sm table-centered table-modern border-0 mt-0">
                <thead class="thead-light">
                  <tr>
                    <th class="text-center">ระดับ</th>
                    <th class="text-center">กรมธรรม์</th>
                    <th class="text-center">บริษัท</th>
                    <th class="text-center">ผู้เอาประกัน</th>
                    <th class="text-center">วิธีการชำระ</th>
                    <th class="text-right">ค่าคอมมิชชั่น(รวม VAT)</th>
                    <th class="text-right">ค่าคอมมิชชั่น(ไม่รวม VAT)</th>
                    <!-- <th class="text-right">ค่าธรรมเนียม</th> -->
                    <th class="text-center">ปี/งวด</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in CommDetailList" :key="idx"
                    :class="{ 'text-success': row.CommissionLevel == 'ค่าคอมมิชชั่น' }">
                    <td class="text-center">{{ idx + 1 }}</td>
                    <td class="text-center">{{ row.ContractPolicyNumber }}</td>
                    <td class="text-center">{{ row.InsuranceTxt }}</td>
                    <td class="text-center">{{ row.Firstname }} {{ row.Lastname }}</td>
                    <td class="text-center">{{ row.PeriodTypeTxt }}</td>
                    <td class="text-right">{{ moneyFormatFull(row.Commission) }}</td>
                    <td class="text-right">{{ moneyFormatFull(row.Total) }}</td>
                    <!-- <td class="text-right">{{ row.PaymentCreditCard ? moneyFormatFull(row.PaymentCreditCard) : '-' }}
                    </td> -->
                    <td class="text-center">{{ row.Year }}/{{ row.Period }}</td>
                  </tr>
                  <tr v-if="!CommDetailList || CommDetailList.length === 0">
                    <td colspan="100%" class="table-empty-note">ไม่มีข้อมูล</td>
                  </tr>
                </tbody>
                <tfoot v-if="CommDetailList && CommDetailList.length > 0" class="bg-light text-dark font-weight-bold">
                  <tr>
                    <th colspan="1" class="text-left">รวม</th>
                    <!-- <th colspan="5" class="text-right">
                      {{moneyFormatFull(CommDetailList.reduce((a, b) => a + b.Commission - b.PaymentCreditCard, 0))}}
                    </th>
                    <th colspan="1" class="text-right">
                      {{moneyFormatFull(CommDetailList.reduce((a, b) => a + b.Total - b.PaymentCreditCard, 0))}}
                    </th> -->
                     <th colspan="5" class="text-right">
                      {{moneyFormatFull(CommDetailList.reduce((a, b) => a + b.Commission, 0))}}
                    </th>
                    <th colspan="1" class="text-right">
                      {{moneyFormatFull(CommDetailList.reduce((a, b) => a + b.Total, 0))}}
                    </th>
                    <th></th>
                  </tr>
                </tfoot>
              </table>
            </div>

          </div>
        </div>

      </div>

    </b-modal>
    </div>
  </Layout>
</template>

<script>
// import countTo from 'vue-count-to'
// import DatePicker from 'vue2-datepicker'

// import appConfig from '@src/app.config'

import Layout from '@/components/layouts/main'

import {
  widgetData,
} from './data'

/**
 * Dashboard-1 Component
 */
var weekYear = require('dayjs/plugin/weekYear')
var weekOfYear = require('dayjs/plugin/weekOfYear')
require('dayjs/locale/th')

let self;

export default {
  // page: {
  //   title: 'Dashboard',
  //   meta: [{ name: 'description', content: appConfig.description }],
  // },
  components: {
    Layout,
  },
  data() {
    return {
      widgetData: [],
      title: 'Dashboard',
      IsLoading: false,
      IsLoadingTab: false,
      activeTab: 'pending',
      Year: this.$dayjs().year(),
      YearTh: this.$dayjs().year() + 543,
      YearTables: this.$dayjs().year(),
      YearThTables: this.$dayjs().year() + 543,
      CurrComm: {},
      CommDetailList: [],
      AMLExpiredIn: 0,
      DashbData: {
        AgentId: '',
        Info: {},
        Commissions: [],
        LineFees: [],
        JobPending: {},
        JobPendingList: [],
        PremiumYear: { New: 0, Renew: 0 },
        PremiumYearWithLine: { New: 0, Renew: 0 }
      },
      DashbDataTables: {
        Commissions: [],
        LineFees: [],
        JobPendingList: [],
      },
      items: [
        {
          text: 'SKLife',
          href: '/',
        },
        {
          text: 'Dashboard',
          active: true,
        },
      ],
      state: {
        date: new Date(),
      },

    }
  },

  async beforeCreate() {
  },
  async created() {
    console.log(this.$store.state.Admin)

    if (this.$store.state.Admin.IsForceChangePwd)
      this.$router.push({ name: 'admin-change-password' });

    self = this;
    self.$dayjs.locale('th')
    self.$dayjs.extend(weekOfYear)
    self.$dayjs.extend(weekYear)
    this.weekNumber = self.$dayjs().week();
    this.curMonthNumber = self.$dayjs().format('MM');
    this.curMonthTxt = self.$dayjs().format('MMM');

    this.lastMonth = self.$dayjs().add(-1, 'month');
    this.lastMonthNumber = this.lastMonth.format('MM');
    this.lastMonthTxt = this.lastMonth.format('MMM');

    console.log('lastMonthTxt', self.$dayjs().format('YYYY-MMMM-DD'));

    this.lastYear = self.$dayjs().add(-1, 'year').format("YYYY");

    self.$dayjs.locale('en')
    //self.StatusDatas = self.getAllOrderStatus();
    //console.log(self.getOrderStatus(1))
    // this.getAllOrder();

  },
  mounted() {
    console.log(this.Year)
    this.loadDataDashboard();
    console.log('DashbData', self.DashbData)
    //console.log(this.$store.state.Admin)
  },
  watch: {
    YearTables: function () {
      self.YearThTables = self.YearTables + 543;
    }
  },
  methods: {

    loadDataDashboard() {
      self.IsLoading = true;

      const config = {
        headers: { Authorization: `Bearer ${this.$store.state.Admin.Token}` },
      };

      this.axios.get(process.env.VUE_APP_OFFICEAPI_URL + '/api/dashb?year=' + self.Year, config)
        .then(function (res) {
          if (res.status == 200) {
            console.log(res)

            if (res.data.PremiumYear == null) {
              res.data.PremiumYear = { New: 0, Renew: 0 };
            }

            if (res.data.PremiumYearWithLine == null) {
              res.data.PremiumYearWithLine = { New: 0, Renew: 0 };
            }

            self.DashbData = res.data;
            self.DashbDataTables.Commissions = self.DashbData.Commissions;
            self.DashbDataTables.LineFees = self.DashbData.LineFees;
            self.DashbDataTables.JobPendingList = self.DashbData.JobPendingList;
            self.DashbDataTables.AgentDocs = self.DashbData.AgentDocs;

            self.AMLExpiredIn = Math.floor(self.$dayjs(self.DashbData.Info.SKLBrokerCardExpired).diff(self.$dayjs(), 'day', true));

            self.$store.commit('setCitizenType', res.data.Info.CitizenType)

          } else {
            self.$swal('เกิดข้อผิดพลาด1');
          }
          self.IsLoading = false;
        })
        .catch(function (error) {
          self.IsLoading = false;
          self.$swal('เกิดข้อผิดพลาด2');
        });
    },
    loadDataDashTables() {
      self.IsLoadingTab = true;

      const config = {
        headers: { Authorization: `Bearer ${this.$store.state.Admin.Token}` },
      };

      this.axios.get(process.env.VUE_APP_OFFICEAPI_URL + '/api/dashb/GetDashbTables?year=' + self.YearTables, config)
        .then(function (res) {
          if (res.status == 200) {
            console.log(res)
            self.DashbDataTables = res.data;
          } else {
            self.$swal('เกิดข้อผิดพลาด1');
          }
          self.IsLoadingTab = false;
        })
        .catch(function (error) {
          self.IsLoadingTab = false;
          self.$swal('เกิดข้อผิดพลาด2');
        });

    },

    showCommDetail(data) {
      this.CurrComm = data;

      self.IsLoading = true;

      const config = {
        headers: { Authorization: `Bearer ${this.$store.state.Admin.Token}` },
        params: { CommDate: self.dateFormat(self.CurrComm.CommissionAt) }
      };

      this.axios.get(process.env.VUE_APP_OFFICEAPI_URL + '/api/dashb/getcommdetail', config)
        .then(function (res) {
          if (res.status == 200) {
            console.log(res)
            self.CommDetailList = res.data;
          } else {
            self.$swal('เกิดข้อผิดพลาด');
          }
          self.IsLoading = false;
          self.$bvModal.show('bv-modal-detail');
        })
        .catch(function (error) {
          self.IsLoading = false;
          self.$swal('เกิดข้อผิดพลาด');
        });

    },

    onClickUpload() {
      alert('ss')
    },

    onChangeYear(step) {
      let maxYear = this.$dayjs().year();
      let minYear = 2023;
      let curYear = self.Year;
      self.Year = self.Year + step;

      if (self.Year < minYear || self.Year > maxYear)
        self.Year = curYear;
      else {
        self.YearTh = self.Year + 543;
        self.YearTables = self.Year;
        self.YearThTables = self.Year + 543;
        this.loadDataDashboard();
      }
    },
    calculateTransferNoVat(commissionAfterTax, paymentCreditCard) {
      return (Number(commissionAfterTax) || 0) - (Number(paymentCreditCard) || 0);
    },
    calculateWhtVat(commissionAfterTax) {
      return (Number(commissionAfterTax) || 0) * 0.03;
    },
    calculateTransferVat(commission, paymentCreditCard, commissionAfterTax) {
      return (Number(commission) || 0) - (Number(paymentCreditCard) || 0) - ((Number(commissionAfterTax) || 0) * 0.03);
    },
    getSum(array, key) {
      if (!array || !array.length) return 0;
      return array.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
    },
    getSumTransferNoVat(array) {
      if (!array || !array.length) return 0;
      return array.reduce((sum, item) => sum + ((Number(item.CommissionAfterTax) || 0) - (Number(item.PaymentCreditCard) || 0)), 0);
    },
    getSumWhtVat(array) {
      if (!array || !array.length) return 0;
      return array.reduce((sum, item) => sum + ((Number(item.CommissionAfterTax) || 0) * 0.03), 0);
    },
    getSumTransferVat(array) {
      if (!array || !array.length) return 0;
      return array.reduce((sum, item) => sum + ((Number(item.Commission) || 0) - (Number(item.PaymentCreditCard) || 0) - ((Number(item.CommissionAfterTax) || 0) * 0.03)), 0);
    },
    getStatusBadgeClass(status) {
      const val = (status || '').toString().trim();
      if (val.includes('อนุมัติ') || val.includes('สำเร็จ') || val.includes('จ่ายแล้ว')) {
        return 'badge badge-soft-success px-2 py-1';
      }
      if (val.includes('รอ') || val.includes('เนินการ')) {
        return 'badge badge-soft-warning px-2 py-1';
      }
      if (val.includes('ยกเลิก') || val.includes('ไม่ผ่าน') || val.includes('ปฏิเสธ')) {
        return 'badge badge-soft-danger px-2 py-1';
      }
      return 'badge badge-soft-secondary px-2 py-1';
    }
  },
}
</script>
